"use client";

import {
  AppError,
  IdSchema,
  type Id,
  type IdGenerator,
  type WallClock,
} from "@hisani/core";
import {
  STOPWATCH_ERROR_CODES,
  finishStopwatchSessionRuns,
  getElapsedMilliseconds,
  transitionStopwatchRun,
  type StopwatchCommand,
} from "@hisani/stopwatch";
import { Button, ConfirmationDialog } from "@hisani/ui";
import { Check, Plus, Share2 } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { setPwaReloadBlocked } from "../../pwa-reload-safety";
import { useHydrated } from "../../use-hydrated";
import {
  browserClock,
  browserWallClock,
  copyText,
  newBrowserId,
  playStopwatchSound,
  takeSwipeDeleteHint,
  useWakeLock,
  type StopwatchSoundCue,
  type StopwatchSoundPlayer,
} from "./stopwatch-browser";
import { StopwatchHistory } from "./stopwatch-history";
import {
  deleteStopwatchSession,
  finishStopwatchSession,
  loadStopwatchHistory,
  updateStopwatchTimerComment,
  type CompletedStopwatchSession,
  type PersistedTimerSnapshot,
  type StopwatchHistoryCursor,
} from "./stopwatch-storage";
import { StopwatchCommentDialog } from "./stopwatch-comment-dialog";
import { StopwatchTimerCard } from "./stopwatch-timer-card";
import {
  buildSummary,
  createTimer,
  getConfirmationCopy,
  toPersistedTimer,
  toPersistedTimers,
  type PendingConfirmation,
  type TimerViewModel,
} from "./stopwatch-workspace-model";

const PRERENDER_TIMER_ID = IdSchema.parse(
  "00000000-0000-4000-8000-000000000000",
);

type CommentEditor =
  | {
      readonly kind: "save";
      readonly timerId: string;
      readonly label: string;
    }
  | {
      readonly kind: "edit";
      readonly sessionId: string;
      readonly timerId: string;
      readonly label: string;
      readonly initialComment: string | null;
    };

export function StopwatchWorkspace({
  initialTimerId,
  idGenerator = newBrowserId,
  wallClock = browserWallClock,
  soundPlayer = playStopwatchSound,
}: {
  readonly initialTimerId?: Id;
  readonly idGenerator?: IdGenerator;
  readonly wallClock?: WallClock;
  readonly soundPlayer?: StopwatchSoundPlayer;
}) {
  const t = useTranslations("Stopwatch");
  const locale = useLocale();
  const [timers, setTimers] = useState<readonly TimerViewModel[]>(() => [
    createTimer(
      initialTimerId ??
        (typeof window === "undefined" ? PRERENDER_TIMER_ID : idGenerator()),
    ),
  ]);
  const [confirmation, setConfirmation] = useState<PendingConfirmation | null>(
    null,
  );
  const [workspaceNotice, setWorkspaceNotice] = useState<string | null>(null);
  const [historyNotice, setHistoryNotice] = useState<string | null>(null);
  const [copiedTimerId, setCopiedTimerId] = useState<string | null>(null);
  const [showDeleteHint] = useState(takeSwipeDeleteHint);
  const [history, setHistory] = useState<readonly CompletedStopwatchSession[]>(
    [],
  );
  const [historyCursor, setHistoryCursor] =
    useState<StopwatchHistoryCursor | null>(null);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [isFinishing, setIsFinishing] = useState(false);
  const [commentEditor, setCommentEditor] = useState<CommentEditor | null>(
    null,
  );
  const [isSavingComment, setIsSavingComment] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);
  const completedSessionDraftRef = useRef<CompletedStopwatchSession | null>(
    null,
  );
  const savingTimerIdsRef = useRef(new Set<string>());
  const [savingTimerIds, setSavingTimerIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [, renderFrame] = useState(0);
  const hydrated = useHydrated();
  const hasRunningTimer = timers.some(({ run }) => run.status === "running");
  const blocksPwaReload =
    isFinishing ||
    isSavingComment ||
    timers.some(({ run }) => run.status !== "idle");

  useEffect(() => {
    setPwaReloadBlocked(blocksPwaReload);
    return () => setPwaReloadBlocked(false);
  }, [blocksPwaReload]);

  useEffect(() => {
    if (copiedTimerId === null) {
      return;
    }
    const timeoutId = window.setTimeout(() => setCopiedTimerId(null), 4_000);
    return () => window.clearTimeout(timeoutId);
  }, [copiedTimerId]);

  useEffect(() => {
    if (!hasRunningTimer) {
      return;
    }

    let frameId = 0;
    const refresh = () => {
      renderFrame((frame) => frame + 1);
      frameId = window.requestAnimationFrame(refresh);
    };
    frameId = window.requestAnimationFrame(refresh);

    return () => window.cancelAnimationFrame(frameId);
  }, [hasRunningTimer]);

  useWakeLock(hasRunningTimer, t("wakeLockUnavailable"), setWorkspaceNotice);

  useEffect(() => {
    let cancelled = false;
    loadStopwatchHistory()
      .then((page) => {
        if (!cancelled) {
          setHistory(page.sessions);
          setHistoryCursor(page.nextCursor);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setHistoryNotice(t("historyLoadFailed"));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setHistoryLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [t]);

  const playCue = useCallback(
    (cue: StopwatchSoundCue) => {
      void soundPlayer(cue).catch(() => {
        setWorkspaceNotice(t("soundUnavailable"));
      });
    },
    [soundPlayer, t],
  );

  const applyCommand = useCallback(
    (id: string, command: StopwatchCommand) => {
      const timer = timers.find(({ run }) => run.id === id);
      if (timer === undefined) {
        return;
      }
      try {
        const transitionedRun = transitionStopwatchRun(
          timer.run,
          command,
          browserClock,
        );
        setTimers((current) =>
          current.map((timer) =>
            timer.run.id === id
              ? {
                  ...timer,
                  run: transitionedRun,
                }
              : timer,
          ),
        );
        setWorkspaceNotice(null);
        const cue = soundCueForCommand(command);
        if (cue !== null) {
          playCue(cue);
        }
      } catch {
        setWorkspaceNotice(t("invalidAction"));
      }
    },
    [playCue, t, timers],
  );

  const addTimer = () => {
    setTimers((current) => [...current, createTimer(idGenerator())]);
    setWorkspaceNotice(null);
  };

  const performRemoveTimer = (id: string) => {
    setTimers((current) => {
      const remaining = current.filter(({ run }) => run.id !== id);
      return remaining.length > 0 ? remaining : [createTimer(idGenerator())];
    });
  };

  const requestResetTimer = (timer: TimerViewModel) => {
    if (
      getElapsedMilliseconds(timer.run, browserClock) > 0 ||
      timer.run.sectionals.length > 0
    ) {
      setConfirmation({ kind: "reset", timerId: timer.run.id });
      return;
    }
    applyCommand(timer.run.id, { type: "RESET" });
  };

  const updateDistance = (id: string, nextSplitDistanceMeters: number) => {
    setTimers((current) =>
      current.map((timer) =>
        timer.run.id === id ? { ...timer, nextSplitDistanceMeters } : timer,
      ),
    );
  };

  const shareSummary = async (
    shareableSummary: string,
    setFeedback: (message: string) => void,
  ) => {
    if (typeof navigator.share !== "function") {
      try {
        await copyText(shareableSummary);
        setFeedback(t("shareFallback"));
      } catch {
        setFeedback(t("copyFailed"));
      }
      return;
    }

    try {
      await navigator.share({
        text: shareableSummary,
        title: t("summaryTitle"),
      });
      setFeedback(t("shared"));
    } catch (error: unknown) {
      if (!(error instanceof DOMException && error.name === "AbortError")) {
        setFeedback(t("copyFailed"));
      }
    }
  };

  const copyTimer = async (timer: TimerViewModel, index: number) => {
    try {
      await copyText(
        buildSummary([toPersistedTimer(timer)], locale, t, {
          dateEpochMilliseconds: wallClock.now().getTime(),
          firstTimerNumber: index + 1,
        }),
      );
      setCopiedTimerId(timer.run.id);
      setWorkspaceNotice(t("copied"));
    } catch {
      setCopiedTimerId(null);
      setWorkspaceNotice(t("copyFailed"));
    }
  };

  const shareTimer = (timer: TimerViewModel, index: number) =>
    shareSummary(
      buildSummary([toPersistedTimer(timer)], locale, t, {
        dateEpochMilliseconds: wallClock.now().getTime(),
        firstTimerNumber: index + 1,
      }),
      setWorkspaceNotice,
    );

  const shareSession = () =>
    shareSummary(
      buildSummary(toPersistedTimers(timers), locale, t, {
        dateEpochMilliseconds: wallClock.now().getTime(),
      }),
      setWorkspaceNotice,
    );

  const finishSession = async () => {
    setIsFinishing(true);
    try {
      const finishedRuns = finishStopwatchSessionRuns(
        timers.map(({ run }) => run),
        browserClock,
      );
      const completedSessionDraft = completedSessionDraftRef.current;
      const completedSession: CompletedStopwatchSession = {
        id: completedSessionDraft?.id ?? idGenerator(),
        completedAtEpochMilliseconds: wallClock.now().getTime(),
        timers: [
          ...(completedSessionDraft?.timers ?? []),
          ...finishedRuns.map((run): PersistedTimerSnapshot => ({
            comment: null,
            run: {
              id: run.id,
              label: run.label,
              status: "stopped",
              elapsedMilliseconds: run.elapsedMilliseconds,
              sectionals: run.sectionals,
            },
          })),
        ],
      };

      await finishStopwatchSession(completedSession);
      completedSessionDraftRef.current = null;
      setHistory((current) => [
        completedSession,
        ...current.filter(({ id }) => id !== completedSession.id),
      ]);
      setTimers([createTimer(idGenerator())]);
      setWorkspaceNotice(t("sessionSavedLocal"));
      playCue("finish");
    } catch (error: unknown) {
      setWorkspaceNotice(
        error instanceof AppError &&
          error.code === STOPWATCH_ERROR_CODES.emptySession
          ? t("emptySession")
          : t("finishFailed"),
      );
    } finally {
      setIsFinishing(false);
    }
  };

  const saveTimer = async (
    timer: TimerViewModel,
    comment: string | null = null,
  ): Promise<boolean> => {
    if (timer.run.status !== "stopped" || savingTimerIdsRef.current.size > 0) {
      return false;
    }

    savingTimerIdsRef.current.add(timer.run.id);
    setSavingTimerIds(new Set(savingTimerIdsRef.current));
    const completedSessionDraft = completedSessionDraftRef.current;
    const completedSession: CompletedStopwatchSession = {
      id: completedSessionDraft?.id ?? idGenerator(),
      completedAtEpochMilliseconds: wallClock.now().getTime(),
      timers: [
        ...(completedSessionDraft?.timers ?? []),
        toPersistedTimer(timer, comment),
      ],
    };

    try {
      await finishStopwatchSession(completedSession);
      completedSessionDraftRef.current = completedSession;
      setHistory((current) => [
        completedSession,
        ...current.filter(({ id }) => id !== completedSession.id),
      ]);
      const replacement = createTimer(idGenerator());
      setTimers((current) =>
        current.map((currentTimer) => {
          if (currentTimer.run.id !== timer.run.id) {
            return currentTimer;
          }
          return {
            ...replacement,
            run: {
              ...replacement.run,
              label: currentTimer.run.label,
            },
            nextSplitDistanceMeters: currentTimer.nextSplitDistanceMeters,
          };
        }),
      );
      const timerNumber =
        timers.findIndex(({ run }) => run.id === timer.run.id) + 1;
      setWorkspaceNotice(
        t("timerSavedLocal", {
          label:
            timer.run.label?.trim() ||
            t("timerDefault", { number: Math.max(1, timerNumber) }),
        }),
      );
      playCue("finish");
      return true;
    } catch {
      setWorkspaceNotice(t("timerSaveFailed"));
      return false;
    } finally {
      savingTimerIdsRef.current.delete(timer.run.id);
      setSavingTimerIds(new Set(savingTimerIdsRef.current));
    }
  };

  const openSaveComment = (timer: TimerViewModel, index: number) => {
    const label =
      timer.run.label?.trim() || t("timerDefault", { number: index + 1 });
    setCommentError(null);
    setCommentEditor({
      kind: "save",
      timerId: timer.run.id,
      label,
    });
  };

  const openHistoryComment = (
    session: CompletedStopwatchSession,
    timer: PersistedTimerSnapshot,
    index: number,
  ) => {
    const label =
      timer.run.label?.trim() || t("timerDefault", { number: index + 1 });
    setCommentError(null);
    setCommentEditor({
      kind: "edit",
      sessionId: session.id,
      timerId: timer.run.id,
      label,
      initialComment: timer.comment,
    });
  };

  const submitComment = async (comment: string | null) => {
    if (commentEditor === null) {
      return;
    }

    setIsSavingComment(true);
    try {
      if (commentEditor.kind === "save") {
        const timer = timers.find(
          ({ run }) => run.id === commentEditor.timerId,
        );
        if (timer === undefined || !(await saveTimer(timer, comment))) {
          setCommentError(t("timerSaveFailed"));
          return;
        }
      } else {
        const parsedComment = await updateStopwatchTimerComment(
          commentEditor.sessionId,
          commentEditor.timerId,
          comment,
        );
        const updateSession = (
          session: CompletedStopwatchSession,
        ): CompletedStopwatchSession =>
          session.id !== commentEditor.sessionId
            ? session
            : {
                ...session,
                timers: session.timers.map((timer) =>
                  timer.run.id === commentEditor.timerId
                    ? { ...timer, comment: parsedComment }
                    : timer,
                ),
              };
        setHistory((current) => current.map(updateSession));
        if (completedSessionDraftRef.current !== null) {
          completedSessionDraftRef.current = updateSession(
            completedSessionDraftRef.current,
          );
        }
        setHistoryNotice(t("commentUpdated"));
      }
      setCommentEditor(null);
    } catch {
      setCommentError(t("commentUpdateFailed"));
    } finally {
      setIsSavingComment(false);
    }
  };

  const requestFinishSession = () => {
    if (hasRunningTimer) {
      setConfirmation({ kind: "finish" });
      return;
    }
    void finishSession();
  };

  const copyHistorySession = async (session: CompletedStopwatchSession) => {
    try {
      await copyText(
        buildSummary(session.timers, locale, t, {
          dateEpochMilliseconds: session.completedAtEpochMilliseconds,
        }),
      );
      setHistoryNotice(t("copied"));
    } catch {
      setHistoryNotice(t("copyFailed"));
    }
  };

  const shareHistorySession = (session: CompletedStopwatchSession) =>
    shareSummary(
      buildSummary(session.timers, locale, t, {
        dateEpochMilliseconds: session.completedAtEpochMilliseconds,
      }),
      setHistoryNotice,
    );

  const deleteHistorySession = async (session: CompletedStopwatchSession) => {
    try {
      await deleteStopwatchSession(session.id);
      if (completedSessionDraftRef.current?.id === session.id) {
        completedSessionDraftRef.current = null;
      }
      setHistory((current) => current.filter(({ id }) => id !== session.id));
      setHistoryNotice(t("sessionDeleted"));
    } catch {
      setHistoryNotice(t("deleteFailed"));
    }
  };

  const requestDeleteHistorySession = (session: CompletedStopwatchSession) => {
    setConfirmation({ kind: "delete", session });
  };

  const confirmPendingAction = () => {
    const pendingConfirmation = confirmation;
    setConfirmation(null);
    if (pendingConfirmation === null) {
      return;
    }
    switch (pendingConfirmation.kind) {
      case "reset":
        applyCommand(pendingConfirmation.timerId, { type: "RESET" });
        return;
      case "finish":
        void finishSession();
        return;
      case "delete":
        void deleteHistorySession(pendingConfirmation.session);
        return;
    }
  };

  const loadMoreHistory = async () => {
    if (historyCursor === null || historyLoading) {
      return;
    }
    setHistoryLoading(true);
    try {
      const page = await loadStopwatchHistory(historyCursor);
      setHistory((current) => [...current, ...page.sessions]);
      setHistoryCursor(page.nextCursor);
    } catch {
      setHistoryNotice(t("historyLoadFailed"));
    } finally {
      setHistoryLoading(false);
    }
  };

  const canFinishSession = timers.some(({ run }) => run.status !== "idle");
  const confirmationCopy = getConfirmationCopy(confirmation, t);

  return (
    <>
      <div className="@container/stopwatch">
        <section
          id="stopwatch-live-workspace"
          className="mx-auto w-full max-w-app bg-canvas px-4 pt-0 pb-8 @min-[40rem]/stopwatch:px-6 @min-[64rem]/stopwatch:px-8"
          aria-label={t("sessionTitle")}
        >
          <div className={timers.length > 1 ? undefined : "mx-auto max-w-4xl"}>
            <div
              className={
                timers.length > 1
                  ? "grid gap-2 @min-[40rem]/stopwatch:gap-5 @min-[80rem]/stopwatch:grid-cols-2"
                  : "grid gap-5"
              }
              aria-label={t("timerList")}
            >
              {timers.map((timer, index) => (
                <StopwatchTimerCard
                  key={timer.run.id}
                  timer={timer}
                  index={index}
                  locale={locale}
                  showDeleteHint={showDeleteHint && index === 0}
                  onCommand={applyCommand}
                  onCopy={() => void copyTimer(timer, index)}
                  onShare={() => void shareTimer(timer, index)}
                  onDelete={() => performRemoveTimer(timer.run.id)}
                  isCopied={copiedTimerId === timer.run.id}
                  onDistanceChange={updateDistance}
                  onReset={requestResetTimer}
                  onComment={(timer) => openSaveComment(timer, index)}
                  onSave={(timer) => void saveTimer(timer)}
                  isSaving={savingTimerIds.has(timer.run.id)}
                  saveDisabled={
                    isFinishing ||
                    isSavingComment ||
                    (savingTimerIds.size > 0 &&
                      !savingTimerIds.has(timer.run.id))
                  }
                />
              ))}
            </div>

            <div className="mt-5">
              <Button
                variant="secondary"
                size="lg"
                className="min-h-14 w-full border-dashed text-base"
                onClick={addTimer}
                disabled={!hydrated}
              >
                <span className="me-2 inline-flex size-8 items-center justify-center rounded-full bg-subtle">
                  <Plus className="size-5" aria-hidden="true" />
                </span>
                {t("addTimer")}
              </Button>
            </div>

            <div className="mt-6 grid gap-3 @min-[40rem]/stopwatch:grid-cols-2">
              <Button
                variant="primary"
                size="lg"
                className="w-full"
                onClick={requestFinishSession}
                disabled={!canFinishSession || savingTimerIds.size > 0}
                loading={isFinishing}
              >
                <Check className="me-2 size-5" aria-hidden="true" />
                {isFinishing ? t("finishingSession") : t("finishSession")}
              </Button>
              <Button
                variant="secondary"
                size="lg"
                className="w-full"
                onClick={() => void shareSession()}
                aria-label={t("shareSession")}
              >
                <Share2 className="me-2 size-4" aria-hidden="true" />
                {t("share")}
              </Button>
            </div>

            <p
              className="mt-5 min-h-6 text-sm font-medium text-secondary"
              role="status"
              aria-live="polite"
            >
              {workspaceNotice}
            </p>
          </div>
        </section>
      </div>

      <div className="@container/stopwatch mx-auto mt-auto w-full max-w-app md:mt-0">
        <div className="px-4 md:pb-8 @min-[40rem]/stopwatch:px-6 @min-[64rem]/stopwatch:px-8">
          <StopwatchHistory
            sessions={history}
            locale={locale}
            isLoading={historyLoading}
            hasMore={historyCursor !== null}
            notice={historyNotice}
            onCopy={(session) => void copyHistorySession(session)}
            onShare={(session) => void shareHistorySession(session)}
            onDelete={requestDeleteHistorySession}
            onEditComment={openHistoryComment}
            onLoadMore={() => void loadMoreHistory()}
          />
        </div>
      </div>

      {commentEditor === null ? null : (
        <StopwatchCommentDialog
          title={
            commentEditor.kind === "edit"
              ? t("editCommentTitle", { label: commentEditor.label })
              : t("saveWithCommentTitle", { label: commentEditor.label })
          }
          description={
            commentEditor.kind === "edit"
              ? t("editCommentDescription")
              : t("saveWithCommentDescription")
          }
          initialComment={
            commentEditor.kind === "edit" ? commentEditor.initialComment : null
          }
          submitLabel={
            commentEditor.kind === "edit"
              ? t("saveComment")
              : t("saveWithComment")
          }
          submitting={isSavingComment}
          error={commentError}
          onClose={() => {
            setCommentEditor(null);
            setCommentError(null);
          }}
          onSubmit={(comment) => void submitComment(comment)}
        />
      )}

      <ConfirmationDialog
        open={confirmationCopy !== null}
        title={confirmationCopy?.title ?? ""}
        description={confirmationCopy?.description ?? ""}
        confirmLabel={confirmationCopy?.confirmLabel ?? ""}
        cancelLabel={t("cancel")}
        closeLabel={t("closeDialog")}
        pending={isFinishing}
        onConfirm={confirmPendingAction}
        onCancel={() => setConfirmation(null)}
      />
    </>
  );
}

function soundCueForCommand(
  command: StopwatchCommand,
): StopwatchSoundCue | null {
  switch (command.type) {
    case "START":
      return "start";
    case "PAUSE":
      return "pause";
    case "RESUME":
      return "resume";
    case "RECORD_SECTIONAL":
      return "split";
    case "STOP":
      return "stop";
    case "RESET":
      return "reset";
    case "RENAME":
      return null;
  }
}
